import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, PhoneOff, AlertCircle, Loader2, Sparkles } from "lucide-react";
import AnimatedVerasistLogo from "./AnimatedVerasistLogo";
import { useT } from "../context/LanguageContext";

const STATUS_LABELS = {
  idle: "Hazır",
  connecting: "Bağlanıyor...",
  connected: "Bağlı",
  failed: "Hata",
};

const Waveform = ({ active }) => (
  <div className="flex items-center gap-0.5 h-4">
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <span
        key={i}
        className="inline-block rounded-full"
        style={{
          width: 2,
          height: active ? `${6 + ((i * 13) % 12)}px` : 3,
          background: "var(--brand-cyan)",
          transition: "height 0.25s ease",
          animation: active
            ? `wf ${0.6 + (i % 3) * 0.15}s ease-in-out ${i * 0.05}s infinite alternate`
            : "none",
        }}
      />
    ))}
    <style>{`@keyframes wf { 0% { height: 3px; } 100% { height: 14px; } }`}</style>
  </div>
);

const VoiceAgentModal = ({ open, onClose }) => {
  const t = useT();
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [widgetReady, setWidgetReady] = useState(false);
  const widgetReadyRef = useRef(false);
  const subscribedRef = useRef(false);
  const startingRef = useRef(false);
  const attemptsRef = useRef(0);
  const MAX_ATTEMPTS = 50;

  // ── Wait for widget + subscribe + auto-start ──
  useEffect(() => {
    if (!open) {
      widgetReadyRef.current = false;
      subscribedRef.current = false;
      attemptsRef.current = 0;
      startingRef.current = false;
      setWidgetReady(false);
      setStatus("idle");
      setErrorMsg("");
      return;
    }

    let cleanupTimer = null;

    const waitForWidget = () => {
      attemptsRef.current += 1;

      if (attemptsRef.current > MAX_ATTEMPTS) {
        setStatus("failed");
        setErrorMsg("Widget yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.");
        console.error("[VoiceAgentModal] Widget did not initialize");
        return;
      }

      const w = window.VerasistVoiceWidget;
      if (!w) {
        cleanupTimer = setTimeout(waitForWidget, 300);
        return;
      }

      try {
        const st = w.getState ? w.getState() : null;
        if (!st || !st.isInitialized) {
          cleanupTimer = setTimeout(waitForWidget, 300);
          return;
        }
      } catch (_) {
        cleanupTimer = setTimeout(waitForWidget, 300);
        return;
      }

      // Widget ready — subscribe & auto-start
      widgetReadyRef.current = true;
      setWidgetReady(true);

      if (!subscribedRef.current) {
        subscribedRef.current = true;

        w.onStatusChange((newStatus) => {
          setStatus(newStatus);
          if (newStatus !== "failed") setErrorMsg("");
        });

        w.onCallEnd(() => {
          setStatus("idle");
        });

        w.onError((err) => {
          setStatus("failed");
          setErrorMsg(
            typeof err === "string" ? err : err?.message || "Bir hata oluştu"
          );
        });
      }

      // Auto-start — modal açılır açılmaz bağlan
      if (!startingRef.current) {
        startingRef.current = true;
        setStatus("connecting");
        w.start();
      }
    };

    waitForWidget();

    return () => {
      if (cleanupTimer) clearTimeout(cleanupTimer);
    };
  }, [open]);

  // ── End call when modal closes ──
  useEffect(() => {
    if (!open && (status === "connected" || status === "connecting")) {
      window.VerasistVoiceWidget?.end();
    }
  }, [open, status]);

  // ── Sonlandır butonu ──
  const handleEndCall = useCallback(() => {
    window.VerasistVoiceWidget?.end();
  }, []);

  const isLive = status === "connected" || status === "connecting";
  const isFailed = status === "failed";

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(15,21,48,0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <style>{`
        #verasist-widget-root { display: none !important; }
      `}</style>

      <div
        className="relative w-full max-w-sm rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: "var(--bg)",
          border: "1px solid var(--border)",
          maxHeight: "92vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:opacity-70 z-10"
          style={{ color: "var(--fg)" }}
          aria-label="Kapat"
        >
          <X size={18} />
        </button>

        {/* ── Animated logo + status ── */}
        <div className="px-6 pt-10 pb-8 flex flex-col items-center text-center">
          {/* Logo — always animated when live, static when idle/failed */}
          <div className="mb-4">
            <AnimatedVerasistLogo
              size={140}
              animated={isLive && status !== "failed"}
            />
          </div>

          {/* Status badge */}
          <span
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1 rounded-full mb-3"
            style={{
              background: isLive
                ? "rgba(46,204,113,0.15)"
                : isFailed
                  ? "rgba(231,76,60,0.15)"
                  : "rgba(31,188,232,0.12)",
              color: isLive ? "#2ecc71" : isFailed ? "var(--brand-coral)" : "var(--brand-cyan)",
            }}
          >
            {isLive ? (
              <><Sparkles size={12} /> {STATUS_LABELS[status]}</>
            ) : isFailed ? (
              <><AlertCircle size={12} /> Hata</>
            ) : (
              <><Loader2 size={12} className="animate-spin" /> Bağlanıyor...</>
            )}
          </span>

          {/* Status text */}
          <div className="text-center space-y-1">
            {status === "connecting" && (
              <>
                <p className="text-[15px] font-semibold" style={{ color: "var(--fg)" }}>
                  {t("voiceAgent.title")}
                </p>
                <p className="text-[13px]" style={{ color: "var(--muted)" }}>
                  Asistana bağlanılıyor, lütfen bekleyin...
                </p>
              </>
            )}
            {status === "connected" && (
              <>
                <p className="text-[15px] font-semibold" style={{ color: "var(--fg)" }}>
                  Görüşme devam ediyor
                </p>
                <div className="flex items-center justify-center gap-2 text-[12.5px]" style={{ color: "var(--muted)" }}>
                  <Waveform active={true} />
                  <span>{t("voiceAgent.speaking")}</span>
                </div>
              </>
            )}
            {status === "failed" && (
              <>
                <p className="text-[15px] font-semibold" style={{ color: "var(--fg)" }}>
                  Bağlantı hatası
                </p>
                <p className="text-[13px]" style={{ color: "var(--brand-coral)" }}>
                  {errorMsg || "Bağlantı kurulamadı"}
                </p>
              </>
            )}
          </div>
        </div>

        {/* ── End call button (only when connected) ── */}
        <div className="px-6 pb-8 flex flex-col items-center">
          {(status === "connected" || status === "connecting") && (
            <button
              onClick={handleEndCall}
              className="inline-flex items-center justify-center gap-2.5 rounded-full px-8 py-3.5 text-[15px] font-bold transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: "var(--brand-coral)",
                color: "#ffffff",
                boxShadow: "0 0 30px rgba(231,76,60,0.35)",
                minWidth: 180,
              }}
            >
              <PhoneOff size={18} /> Aramayı Sonlandır
            </button>
          )}

          {status === "failed" && (
            <button
              onClick={() => {
                setStatus("connecting");
                setErrorMsg("");
                startingRef.current = true;
                window.VerasistVoiceWidget?.start();
              }}
              className="inline-flex items-center justify-center gap-2.5 rounded-full px-8 py-3.5 text-[15px] font-bold transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: "linear-gradient(135deg, var(--brand-navy), #2A3A78)",
                color: "#ffffff",
                boxShadow: "0 0 30px rgba(31,188,232,0.2)",
                minWidth: 180,
              }}
            >
              <AlertCircle size={18} /> Tekrar Dene
            </button>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 flex items-center justify-center"
          style={{ borderTop: "1px solid var(--border)", background: "var(--card-bg)" }}
        >
          <p className="text-[12.5px]" style={{ color: "var(--muted)" }}>
            {t("voiceAgent.simulation")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoiceAgentModal;
